import {useMemo} from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGlobalConfigState} from '_context'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const sites = require('../../../../config/sites.js')

interface UseSeoUrlsOptions {
    /** Optional path segment appended to the SEO URLs query (e.g. `stories/bc-duo-collection.html`). */
    path?: string
}

interface AlternateUrl {
    hreflang: string
    url: string
}

interface SeoUrlsResult {
    alternate?: Array<AlternateUrl>
    canonical?: string
}

/**
 * @name buildSeoUrls
 *
 * @description
 * Pure function that builds SEO alternate and canonical URLs from the sites configuration.
 * Isomorphic — works in both SSR and client contexts.
 *
 * @param host - The public hostname (e.g. 'dev.shop.brunellocucinelli.com').
 *               Must be the public-facing domain, not the internal MRT/Mobify hostname.
 *               Use the `hostLink` site preference to resolve this.
 * @param localeAlias - The current locale alias (e.g. 'en-it')
 * @param path - Optional path segment (e.g. 'stories/bc-duo-collection.html')
 * @returns SeoUrlsResult with alternate and canonical URLs
 */
function buildSeoUrls(host: string, localeAlias: string, path?: string): SeoUrlsResult {
    if (!sites || !Array.isArray(sites)) {
        return {}
    }

    const baseUrl = `https://${host}`
    const pathSegment = path || ''

    const canonical = pathSegment
        ? `${baseUrl}/${localeAlias}/${pathSegment}`
        : `${baseUrl}/${localeAlias}/`

    const alternate: AlternateUrl[] = sites
        .flatMap(
            (site: {l10n?: {supportedLocales?: Array<{id: string; alias: string}>}}) =>
                site.l10n?.supportedLocales?.map((locale) => ({
                    hreflang: locale.id,
                    url: `${baseUrl}/${locale.alias}/${pathSegment}`,
                })) || [],
        )
        .concat({hreflang: 'x-default', url: `${baseUrl}/${pathSegment}`})

    return {alternate, canonical}
}

/**
 * Resolves the public-facing hostname from the `hostLink` site preference.
 *
 * `hostLink` is a JSON string mapping site IDs to hostnames:
 * `{"bc-eu": "dev.shop.brunellocucinelli.com", "bc-cn": "dev.shop.brunellocucinelli.cn"}`
 *
 * @param hostLink - The raw `hostLink` preference value (JSON string)
 * @param siteId - The current site ID (e.g. 'bc-eu')
 * @returns The resolved hostname or undefined if not available
 */
function resolveHostLink(hostLink: string | undefined | null, siteId: string | undefined): string | undefined {
    if (!hostLink || !siteId) return undefined
    try {
        return JSON.parse(hostLink)[siteId] || undefined
    } catch {
        return undefined
    }
}

/**
 * Rewrites a storefront URL from SFCC to the public `hostLink` domain.
 * Relative URLs and SFCC internal URLs (`/s/...`) are normalized; unrelated absolute URLs are preserved.
 */
function absolutizeSfccUrl(url: string, host: string): string {
    const publicOrigin = `https://${host}`
    const isAbsoluteUrl = /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(url)

    try {
        const parsed = new URL(url, publicOrigin)

        const isInternalSfccUrl = parsed.hostname.endsWith('.commercecloud.salesforce.com')

        // A third-party URL is not a storefront URL and must not be redirected to hostLink.
        if (isAbsoluteUrl && parsed.origin !== publicOrigin && !isInternalSfccUrl) {
            return url
        }

        const pathname = parsed.pathname.replace(/^\/s\//, '/')
        return `${publicOrigin}${pathname}${parsed.search}${parsed.hash}`
    } catch {
        // Keep malformed data unchanged instead of emitting an invented URL.
        return url
    }
}

/** Rewrites URL-bearing JSON-LD fields without mutating the SFCC response object. */
function absolutizeStructuredDataUrls<T>(data: T, host: string): T {
    const walk = (value: unknown): unknown => {
        if (Array.isArray(value)) return value.map(walk)
        if (!value || typeof value !== 'object') return value

        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>).map(([key, child]) => [
                key,
                (key === 'url' || key === 'item') && typeof child === 'string' ? absolutizeSfccUrl(child, host) : walk(child),
            ]),
        )
    }

    return walk(data) as T
}

/**
 * @name useSeoUrls
 *
 * @description
 * Shared hook that builds SEO alternate/canonical URLs using `buildSeoUrls`
 * and the public hostname from the `hostLink` site preference.
 *
 * `hostLink` is read from the global config state (populated by the batched
 * `useCustomPreference` in `_app`) — no additional SCAPI call is made.
 *
 * Returns `undefined` until `hostLink` is available, so callers should not render
 * canonical/alternate tags until this returns a value (avoids emitting wrong or empty URLs).
 *
 * @param options.path - Optional path segment for the page (omit for homepage)
 * @returns SeoUrlsResult with alternate and canonical URLs, or undefined while hostLink is loading
 */
const useSeoUrls = ({path}: UseSeoUrlsOptions = {}): SeoUrlsResult | undefined => {
    const {locale, site} = useMultiSite()
    const {appPreferences} = useGlobalConfigState()
    const hostLinkLocalized = resolveHostLink(appPreferences?.hostLink, site?.id)

    return useMemo(() => {
        if (!hostLinkLocalized) return undefined
        return buildSeoUrls(hostLinkLocalized, locale.alias, path)
    }, [hostLinkLocalized, locale.alias, path])
}

export {useSeoUrls, buildSeoUrls, resolveHostLink, absolutizeSfccUrl, absolutizeStructuredDataUrls}
export type {SeoUrlsResult, AlternateUrl}
